import { MenuItem, TableState } from 'primeng/api';
import { Component, AfterViewInit } from '@angular/core';
import { BaseFormTableComponent } from './base-form-table.component';
import { DomHandler } from 'primeng/dom';
import { FormControl, FormGroup } from '@angular/forms';
import { TableColumn } from '../../../models/table-column.model';

/**
 * 雙檔表單的抽象類別，所謂的雙檔表單就是除了上方有輸入的表單外，下方還有 Table 呈現的畫面。
 * 繼承基本表單 BaseFormComponent，除了原有單檔做的事情外，這裡負責處理 Table 相關控制。
 * 如果不是資料維護的功能，像是查詢，也算是這種類型，只要繼承後設定不需要 Menubar 就可以。
 *
 * @export
 * @abstract
 * @class BaseFormTableComponent
 * @extends {BaseFormComponent}
 * @implements {AfterViewInit}
 */
@Component({
  template: '',
})
export abstract class BaseFormTableInlineEditComponent extends BaseFormTableComponent implements AfterViewInit {

  // ------------ 以下開始為單身 Table Inline Edit 相關 properties -------------

  /**
   * inline edit 輸入表單的 FormGroup。
   * 在 initInlineFormGroup() 才初始化。
   */
  inlineFormGroup!: FormGroup;

  /**
   * 用來記錄表單是否填寫完成按下送出。
   * 提供給畫面判斷要不要顯示欄位驗證錯誤訊息的條件之一。
   */
  inlineSubmitted: boolean = false;

  /**
   * inline edit 是否依賴 master。
   * 當 master 只是查詢條件，inline 編輯與 master 無關的時候，要 override 這個 = false
   */
  isInlineDependentOnMaster: boolean = true;

  /**
   * Table 上方的功能選單 - Add 按下去後預設要做的事情。
   * 如果有在編輯資料，會先觸發該筆資料的 save，然後在該筆資料的下一筆資料插入新增的列。
   * 如果沒有在編輯資料，會在第一筆之前插入新增的列，並跳回第一頁。
   */
  override async detailMenuAddCommand(): Promise<void> {
    // console.log('detailMenuAddCommand');

    // inline 依賴 master 的時候才要先檢查 master 輸入狀態
    if (this.isInlineDependentOnMaster) {
      this.submitted = true;
      if (!this.formGroup.valid) {
        return;
      }
    }

    const saveEditingRow = await this.saveEditingRow();
    if (!saveEditingRow.success) {
      // 如果 save 失敗直接 return 下面都不做了
      return;
    }

    // 要開始進入新增的資料了，所以把 Submitted 恢復成 false
    this.inlineSubmitted = false;

    // 產生新增的資料
    let newRowData: any = this.generateNewRowData();

    // 找出目前編輯的資料
    const targetIndex = this.detailDatas.findIndex(data =>
      (data as any)[this.rowDataKeyName] === saveEditingRow.key);
    if (targetIndex >= 0) {
      // 如果有在編輯資料，在該筆資料的下一筆資料插入新增的列
      this.detailDatas.splice(targetIndex + 1, 0, newRowData);
    } else {
      // 如果沒有在編輯資料，會在第一筆之前插入新增的列
      this.detailDatas.splice(0, 0, newRowData);
    }

    // 但如果有先進行排序，上面資料加在哪裡都沒有用，因為會被重新排序過。
    // 為了解決這個問題，新增的時候就清除 Table 排序的狀態
    // ( 但畫面資料順序還是排序過的，整張單存檔後就會重新恢復未排序的狀態 )
    this.claerTableSort();

    // 設定目前正在編輯的 row
    this.tableComponent.editingRowKeys = { [newRowData[this.rowDataKeyName]]: true };

    // 重設 formGroup
    this.inlineFormGroup.reset();

    // 把資料塞到 formGroup 內
    this.onPatchEditableRow({
      data: newRowData
    }).then(() => {
      // 如果有頁籤分頁要切回第一個頁籤
      if (this.detailTabs.length > 0 && this.detailTabMenuComponent) {
        this.detailTabMenuComponent.activeItem = this.detailTabs[0];
        // 發出 activeItemChange 事件
        this.detailTabMenuComponent.activeItemChange.emit(this.detailTabs[0]);
      }

      // 把頁碼切到目前新增的這筆資料的頁數
      const pageIndex = this.getPageIndexForData();
      // setTimeout 0 是重點，不然分頁的目前頁面顯示不會切換
      setTimeout(() => {
        this.tableComponent.first = pageIndex * this.tableComponent.rows!;
        this.disablePaginator();
      }, 0);

      // focus 第一個可以進入的欄位
      this.focusFirstFocusableElement();

      // 發送資料筆數改變通知
      this.fireRowDataChanged();
    }).catch(response => {
      console.error(response);
    }).finally(() => {
    });
  }

  /**
   * 清除 Table 的排序。
   */
  private claerTableSort(): void {
    // 只要有動到篩選的按鈕就清除 Table 選擇的資料
    this.clearRowSelectedDatas();
    // 還原 Table 狀態
    this.tableComponent.reset();
    // 清除 filter，我看元件 reset() 原始碼裡面有試圖去清除 filterValue，但沒有做完全，或許之後官方會修正就不需要這個了
    this.tableComponent.filters = {};

    // 儲存到 storage 裡的選擇和篩選資料也要清除
    const storage = this.tableComponent.getStorage();

    if (this.tableComponent.stateKey) {
      let state = storage.getItem(this.tableComponent.stateKey);
      let tableState: TableState;
      // 因為 reset 會把 Table 排序都清掉，所以要把他從 storage 寫回來
      if (state) {
        tableState = JSON.parse(state);
        tableState.selection = [];
        tableState.filters = {};
        tableState.sortField = undefined;
        tableState.sortOrder = this.tableComponent.defaultSortOrder;
        storage.setItem(this.tableComponent.stateKey, JSON.stringify(tableState));
      }
    }
  }

  /**
   * 產生並取得 inline edit 要新增的資料。
   * 新增時要預設的資料都做在這。
   *
   * @returns inline edit 要新增的資料
   */
  abstract generateNewRowData(): any;

  /**
   * Table 頁籤切換的事件。
   * 要變更顯示的欄位，並且設定欄位寬度。
   *
   * inline edit 要多做 focus 到第一個可以進入的欄位
   *
   * @param item Newly selected MenuItem
   */
  override onDetailTabActiveItemChange(item: MenuItem) {
    super.onDetailTabActiveItemChange(item);

    // if (!this.detailTabIsChanged) {
    //   return;
    // }

    // focus 第一個可以進入的欄位
    this.focusFirstFocusableElement();
  }

  /**
   * focus 第一個可以進入的欄位
   */
  private focusFirstFocusableElement(): void {
    if (this.isTableInEdit()) {
      setTimeout(() => {
        // 找出 Table 這個頁籤第一個可以 focus 的欄位並 focus
        const tableRef = this.tableComponentRef.nativeElement as HTMLElement;
        const focusableElement = tableRef.querySelector('.p-editable-column input:not([disabled]):not(.bpaas-hidden-focus)');
        if (focusableElement) {
          (focusableElement as HTMLElement).focus();
          this.setComponentButtonIndex(focusableElement);
        } else {
          // 如果整頁的欄位都不可以 focus，就去找隱藏的 bpaas-hidden-focus span 然後 focus
          const hiddenFocusableElement = tableRef.querySelector('.p-editable-column span.bpaas-hidden-focus');
          if (hiddenFocusableElement) {
            (hiddenFocusableElement as HTMLElement).focus();
          }
        }
      }, 0);
    }
  }

  /**
   * 為了解決日期欄位 tab focus 會跳到日曆按鈕或 autoComplete 的下拉按鈕上，把這些按鈕的 tabindex 改為 -1。
   * @param element focus 的欄位
   */
  setComponentButtonIndex(element: any) {
    // 用 focus 的欄位往上找到 p-editable-column 也就是 td 那層
    let editableColumnElement = this.findEditableColumnElement(element);
    // 再往上找到 tr 那層
    const parentElement = editableColumnElement?.parentElement;
    // 找出日曆和下拉選單的按鈕，把這些按鈕的 tabindex 改為 -1。
    const buttonElements = parentElement?.querySelectorAll('button.p-datepicker-trigger, button.p-autocomplete-dropdown');
    if (buttonElements) {
      buttonElements.forEach((element: HTMLElement) => {
        element.setAttribute('tabindex', '-1');
      });
    }
  }

  /**
   * 欄位往上找到 p-editable-column 也就是 td 那層
   *
   * @param element
   * @returns td 那層的物件
   */
  findEditableColumnElement(element: any) {
    if (element) {
        let focusElement = element;
        while (focusElement && !DomHandler.hasClass(focusElement, 'p-editable-column')) {
          focusElement = focusElement.parentElement;
        }
        return focusElement;
    } else {
        return undefined;
    }
  }

  /**
   * 判斷現在 Table 是否在 inline edit 狀態。
   *
   * @returns 是否在 inline edit 狀態
   */
  isTableInEdit(): boolean {
    return this.tableComponent?.editingRowKeys && Object.keys(this.tableComponent.editingRowKeys).length > 0;
  }

  /**
   * inline edit 元件通知變更 formGroup Submitted
   * @param event true or false
   */
  onInlineSubmitted(event: any) {
    this.inlineSubmitted = event;
  }

  /**
   * 按下 Tab 鍵切換輸入欄位的事件
   * 這個主要會用在 table 欄位過多有分頁籤顯示的時候，要去判斷進入下一個頁籤的第一個欄位
   *
   * @param event event.column: currentCell (TD)
   */
  onTab(event: any) {
    // 如果 table 沒有分頁就不需要繼續
    if (this.detailTabs.length == 0 || !this.detailTabMenuComponent) {
      return;
    }

    // 取得當前活動的頁籤
    const activeItem = this.detailTabMenuComponent.activeItem!;
    // 取得當前活動的頁籤裡面有哪些欄位
    const activeTabTableColumns = this.detailTabColumns[activeItem.id!];
    // 當前活動的頁籤裡面的最後一個欄位
    // const lastColumn = activeTabTableColumns[activeTabTableColumns.length - 1];
    const lastColumn = this.getLastCanFocusColumn(activeTabTableColumns, activeTabTableColumns.length - 1);

    // 取得目前按下 tab 的欄位 HTMLElement
    const currentElement = event.column as HTMLElement;
    // 判斷目前欄位是否是該頁籤的最後一個欄位
    if (!lastColumn
        || (lastColumn && currentElement.getAttribute('id')?.endsWith(lastColumn.field.replace(/([A-Z])/g, "-$1").toLowerCase()))) {
      // 如果是最後一個欄位就要跳到第一個頁籤，如果不是，就跳下一個頁籤
      const activeIndex = this.detailTabs.indexOf(activeItem);
      let nextActiveItem = this.detailTabs[activeIndex + 1];
      if (activeIndex == this.detailTabs.length - 1) {
        nextActiveItem = this.detailTabs[0];
      }
      this.detailTabMenuComponent.activeItem = nextActiveItem;
      // 發出 activeItemChange 事件
      this.detailTabMenuComponent.activeItemChange.emit(nextActiveItem);
    }
  }

  /**
   * 取得當前活動的頁籤裡面的最後一個可以 focus 的欄位
   * @param activeTabTableColumns 當前活動的頁籤裡面有哪些欄位
   * @param lastIndex 欄位在頁籤內的 index，從最後一個開始往前找
   * @returns 最後一個可以 focus 的欄位
   */
  private getLastCanFocusColumn(activeTabTableColumns: TableColumn[], lastIndex: number): TableColumn | undefined {
    // 當前活動的頁籤裡面的最後一個欄位
    let lastCanFocusColumn: TableColumn | undefined = activeTabTableColumns[lastIndex];
    if (lastCanFocusColumn && this.inlineFormControl(lastCanFocusColumn.inlineFormControlName ?? lastCanFocusColumn.field).disabled) {
      if (lastIndex >= 0) {
        lastCanFocusColumn = this.getLastCanFocusColumn(activeTabTableColumns, lastIndex - 1);
      } else {
        lastCanFocusColumn = undefined;
      }
    }
    return lastCanFocusColumn;
  }

  /**
   * 找到目前 inline edit 這筆資料所在的頁索引。
   * 這個是用在新增的時候，如果已經在編輯某一筆資料，這時候按下新增，資料會加在該筆資料後面。
   * 但如果這筆資料已經是該頁的最後一筆了，新增的資料就要畫到下一頁的第一筆。
   *
   * @returns 目前 inline edit 這筆資料所在的頁索引
   */
  getPageIndexForData(): number {
    // console.log('getPageIndexForData');
    const key = this.inlineFormGroup.getRawValue()[this.rowDataKeyName];
    const targetIndex = this.detailDatas.findIndex(
      data => (data as any)[this.rowDataKeyName] === key);
    console.log('getPageIndexForData targetIndex = ' + targetIndex);
    if (targetIndex !== -1) {
      return Math.floor(targetIndex / this.tableComponent.rows!);
    }
    return 0; // 找不到指定資料，預設切換到第一頁
  }

  /**
   * 分頁所有元件都變成 disabled 不可按。
   * 因為在 inline edit 的時候再去切換分頁狀況會變得非常複雜，所以開始 inline edit 後，就不能改變分頁了。
   */
  disablePaginator() {
    // 找 p-table tag 裡面的 p-paginator 所有非 disabled 的 button 出來設定 disabled
    const paginatorPages = DomHandler.find(this.tableComponent.el.nativeElement, 'p-paginator button:not(.p-disabled)');
    for (const button of paginatorPages) {
      DomHandler.addMultipleClasses(button, 'bpaas-editable-row-disabled p-disabled');
    }

    // 找 p-table tag 裡面的 p-paginator 所有 p-dropdown 出來設定 disabled，這個是每頁幾筆的下拉選單
    const paginatorPerPage = DomHandler.findSingle(this.tableComponent.el.nativeElement, 'p-paginator p-dropdown');
    if (paginatorPerPage) {
      DomHandler.addMultipleClasses(paginatorPerPage, 'bpaas-editable-row-disabled p-disabled');
    }
  }

  /**
   * 恢復分頁元件為可按。
   */
  enablePaginator() {
    // 找 p-table tag 裡面的 p-paginator 所有因 inline 而 disabled 的物件出來取消 disabled
    const elements = DomHandler.find(this.tableComponent.el.nativeElement, 'p-paginator .bpaas-editable-row-disabled.p-disabled');
    for (const element of elements) {
      DomHandler.removeClass(element, 'bpaas-editable-row-disabled');
      DomHandler.removeClass(element, 'p-disabled');
    }
  }

  /**
   * 進入 inline edit 開始編輯資料時，要呼叫這個實作把資料塞到 formGroup 內。
   *
   * 注意 :
   * (1) 因為 'async' 修飾元無法與 'abstract' 修飾元並用，實作時請自己加上 async。
   * (2) 透過 async / await 保證執行的順序，或用 Promise.all 非同步同時執行多個優化處理時間。
   * (3) 因為有可能在編輯前要去載入或查詢一些其他資料，為了確保資料按照順序執行避免錯誤，所以用 async + Promise
   *
   * @param event event.data: 要編輯的資料
   */
  abstract onPatchEditableRow(event: { data: any }): Promise<any>;

  /**
   * 注意 :
   * (1) 因為 'async' 修飾元無法與 'abstract' 修飾元並用，實作時請自己加上 async。
   * (2) 透過 async / await 保證執行的順序，或用 Promise.all 非同步同時執行多個優化處理時間。
   */
  abstract beforeSaveEditableRow(): Promise<boolean>;

  /**
   * 按下儲存按鈕後，會呼叫這個，這裡面會把目前編輯中的資料作儲存
   */
  async onSaveEditableRow(callback?: Function): Promise<any> {
    const checked = await this.saveEditingRow();
    if (checked.success && callback && typeof callback === 'function') {
      // 如果存在，调用 callback
      callback();
    }
  }

  /**
   * 如果目前已經有在編輯，要先做欄位驗證，沒有問題才能繼續。
   * 正在編輯的欄位驗證有過的話，會先儲存，並結束該筆 inline 狀態。
   *
   * @returns
   */
  async saveEditingRow(): Promise<{ success: boolean, key?: string}> {
    if (!this.isTableInEdit()) {
      return Promise.resolve({ success: true });
    }

    // 如果目前已經有在編輯，要先做欄位驗證，沒有問題才能繼續
    // 因為正在編輯狀態，所以有先把 Submitted 改成 true，讓前端錯誤欄位可以變色
    this.inlineSubmitted = true;
    if (!this.inlineFormGroup.valid) {
      this.focusFirstInvalidField();
      return Promise.resolve({ success: false });
    }

    try {
      // 先做存檔前額外要做的事情
      const canDoSave: boolean = await this.beforeSaveEditableRow();
      if (canDoSave) {
        const saved: boolean = await this.doSaveEditableRow();
        if (!saved) {
          return Promise.resolve({ success: false });
        }
      } else {
        return Promise.resolve({ success: false });
      }

      // 結束目前正在編輯的那筆
      const propNames = Object.getOwnPropertyNames(this.tableComponent.editingRowKeys);
      const key = propNames[0];
      delete this.tableComponent.editingRowKeys[key];

      this.enablePaginator();

      // 重設 FormGroup 狀態，這樣新增的那筆才能判斷 dirty 是否有修改過
      this.inlineFormGroup.reset();

      return Promise.resolve({ success: true, key: key });
    } catch (error) {
      // 如果 Promise 被拒絕，這裡的程式碼將被執行
      console.error('Error:', error);
      return Promise.resolve({ success: false });
    }
  }

  /**
   * 按下儲存按鈕後，要呼叫這個實作把 formGroup 的資料取出來塞到 detailDatas 內。
   */
  abstract doSaveEditableRow(): Promise<boolean>;

  /**
   * 按下取消按鈕後，要呼叫這個實作做什麼額外的事。
   */
  abstract onCancelEditableRow(): void;

  /**
   * 按下刪除按鈕後，從 detailDatas 移除該筆資料。
   * @param event event.data: 要刪除的資料, event.confirm: 要不要詢問
   */
  onDeleteEditableRow(event: any) {
    console.log('deleteEditableRow');

    if (event.confirm == false) {
      // inline edit 的新增資料沒儲存就刪除會跑這段
      this.detailDatas = this.detailDatas.filter(data => {
        return (data as any)[this.rowDataKeyName] !== event.data[this.rowDataKeyName]
      });

      // 資料刪除後，發送資料筆數改變通知
      this.fireRowDataChanged();
    } else {
      const deleteHint = this.rowDataDeleteHintFieldName ? event.data[this.rowDataDeleteHintFieldName] : '';

      // 從這裡刪的一定是清除不是刪除
      this.systemConfirmationService.confirmClear(
        () => {
          this.detailDatas = this.detailDatas.filter(data => {
            return (data as any)[this.rowDataKeyName] !== event.data[this.rowDataKeyName]
          });

          // 清除已選擇 Row
          this.clearRowSelectedDatas();

          // 資料刪除後還要做什麼
          this.afterDeleteEditableRow(event.data);

          // 資料刪除後，發送資料筆數改變通知
          this.fireRowDataChanged();
        },
        deleteHint
      );
    }
  }

  /**
   * 按下刪除按鈕後，從 detailDatas 移除資料後還要做什麼。
   * @param deletedData 刪除的資料
   */
  abstract afterDeleteEditableRow(deletedData: any): void;

  /**
   * Table 篩選資料的事件。
   * 如果有正在編輯的資料要先取消。
   *
   * @param event
   */
  override onDetailTableFilter(event: any): void {
    super.onDetailTableFilter(event);

    if (this.isTableInEdit()) {
      const btnCancelEditableRow = DomHandler.findSingle(this.tableComponent.el.nativeElement, '#' + this.parseToId('btnCancelEditableRow'));
      if (btnCancelEditableRow) {
        btnCancelEditableRow.click();
      }
    }
  }

  /**
   * Focus inline edit 第一個錯誤的欄位。
   */
  focusFirstInvalidField(): void {
    // console.log('focusFirstInvalidField');
    // 找到第一個錯誤的欄位名稱
    let firstInvalidFieldName;
    for (const controlName in this.inlineFormGroup.controls) {
      if (this.inlineFormGroup.controls.hasOwnProperty(controlName)) {
        const control = this.inlineFormGroup.controls[controlName];
        if (control.errors) {
          control.markAsDirty();
          firstInvalidFieldName = controlName;
          break;
        }
      }
    }

    console.log('focusFirstInvalidField firstInvalidFieldName = ' + firstInvalidFieldName);
    if (firstInvalidFieldName) {
      if (this.detailTabs.length > 0 && this.detailTabMenuComponent) {
        // 切換到第一個錯誤欄位所在的頁籤
        const menuItem = this.findMenuItemByFieldName(firstInvalidFieldName);
        if (menuItem) {
          this.detailTabMenuComponent.activeItem = menuItem;
          // 發出 activeItemChange 事件
          this.detailTabMenuComponent.activeItemChange.emit(menuItem);
        }
      }

      setTimeout(() => {
        let firstInvalidElement = this.findFirstInvalidElement();
        if (firstInvalidElement) {
          if (firstInvalidElement.tagName.toLowerCase() === 'input') {
            firstInvalidElement.focus();
          } else {
            const focusableElement = DomHandler.find(firstInvalidElement, 'input:not([disabled])');
            // console.dir(focusableElement[0]);
            if (focusableElement[0]) {
              focusableElement[0].focus();
            }
          }
        }
      }, 0);
    }
  }

  /**
   * 找現在 inline edit 裡第一個錯誤的物件
   * @returns inline edit 裡第一個錯誤的物件
   */
  findFirstInvalidElement() {
    if (!this.isTableInEdit()) {
      return undefined;
    }
    const invalidElements = DomHandler.find(this.tableComponent.el.nativeElement, '.ng-invalid.ng-dirty');
    const filteredElements = invalidElements.filter(element => !element.matches('tr'));
    // console.log('findFirstInvalidField filteredElements');
    // console.dir(filteredElements);
    return filteredElements ? filteredElements[0] : undefined;
  }

  /**
   * 找到欄位所在的頁籤
   *
   * @param fieldName 欄位名稱
   * @returns 所在的頁籤
   */
  findMenuItemByFieldName(fieldName: string): MenuItem | undefined {
    let menuItem;
    for (const tabId in this.detailTabColumns) {
      if (this.detailTabColumns.hasOwnProperty(tabId)) {
        const columns = this.detailTabColumns[tabId];
        const column = columns.find(col => col.field === fieldName);
        if (column) {
          menuItem = this.detailTabs.find(tab => tab.id === tabId);
          break;
        }
      }
    }
    return menuItem;
  }

  /**
   * inlineFormControl 共用 function，用來提供 html 判斷欄位是否有錯誤。
   * @param formControlName inlineFormControl 的名稱
   * @returns boolean 欄位是否有錯誤
   */
  inlineFormControlInvalid(formControlName: string): boolean {
    const formControl = this.inlineFormGroup.get(formControlName);
    if (formControl) {
      return formControl.invalid && (formControl.dirty || this.inlineSubmitted);
    } else {
      return false;
    }
  }

  /**
   * inlineFormControl 共用 function，用來提供 html 取 FormControl。
   * @param formControlName inlineFormControl 的名稱
   * @returns FormControl
   */
  inlineFormControl(formControlName: string): FormControl {
    return this.inlineFormGroup.get(formControlName) as FormControl;
  }

  /**
   * 清除 inlineFormControl 值。
   * @param formControlName inlineFormControl 的名稱
   */
  clearInlineFormControlValue(formControlName: string): void {
    const formControl = this.inlineFormGroup.get(formControlName);
    if (formControl) {
      formControl.setValue(null);
    }
  }
}
